import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Lock, PartyPopper } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

const WHEEL_COLORS = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
    '#f59e0b', '#10b981', '#06b6d4', '#3b82f6', '#a855f7',
];

/** Split long label into two lines at nearest space to middle for readable wrap */
function splitLabel(text, maxChars = 14) {
    const t = String(text).trim();
    if (t.length <= maxChars) return [t];
    const mid = Math.floor(t.length / 2);
    const left = t.lastIndexOf(' ', mid);
    const right = t.indexOf(' ', mid);
    const splitAt = left >= 0 && (right < 0 || mid - left <= right - mid) ? left : right;
    if (splitAt > 0 && splitAt < t.length) {
        return [t.slice(0, splitAt).trim(), t.slice(splitAt + 1).trim()];
    }
    return [t.slice(0, mid), t.slice(mid)];
}

const SpinnerWheelForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null }) => {
    const segments = content?.segments || ['Option 1', 'Option 2', 'Option 3'];
    const [spinning, setSpinning] = useState(false);
    // The winning segment is drawn server-side (see responseController.submitResponse) so it
    // can't be gamed from devtools — the wheel waits for that draw to come back before it
    // starts spinning, then animates to the index the server chose.
    const [waitingForServer, setWaitingForServer] = useState(false);
    const [result, setResult] = useState(null);
    const [rotation, setRotation] = useState(0);
    const wheelRef = useRef(null);
    const waitTimeoutRef = useRef(null);

    const allowSpinAgain = content?.settings?.allowSpinAgain !== false;
    const hasSubmitted = userAnswer != null;
    const displayResult = result ?? (waitingForServer ? null : (userAnswer?.selected ?? userAnswer?.spinResult ?? null));
    const spinDisabled = isLocked || spinning || waitingForServer || (hasSubmitted && !allowSpinAgain);

    const animateTo = useCallback((winIndex) => {
        const spins = 5 + Math.floor(Math.random() * 5);
        const segAngle = 360 / segments.length;
        const targetAngle = (360 - (winIndex * segAngle + segAngle / 2) + 360) % 360;

        setSpinning(true);
        setRotation(prev => {
            const prevMod = ((prev % 360) + 360) % 360;
            const delta = (targetAngle - prevMod + 360) % 360;
            const totalRotation = delta + spins * 360;
            return prev + totalRotation;
        });

        setTimeout(() => {
            setSpinning(false);
            setResult(segments[winIndex]);
        }, 4000);
    }, [segments]);

    // Once the server's draw arrives (userAnswer updates with a fresh index) while we're
    // waiting on one, kick off the landing animation toward that segment.
    useEffect(() => {
        if (!waitingForServer || userAnswer?.index == null) return;
        if (waitTimeoutRef.current) { clearTimeout(waitTimeoutRef.current); waitTimeoutRef.current = null; }
        setWaitingForServer(false);
        animateTo(userAnswer.index);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waitingForServer, userAnswer]);

    useEffect(() => () => { if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current); }, []);

    const spin = useCallback(() => {
        if (spinning || waitingForServer || isLocked || (hasSubmitted && !allowSpinAgain)) return;
        setResult(null);
        if (isPreview) {
            // No server round trip in preview mode — spin to a random segment immediately.
            animateTo(Math.floor(Math.random() * segments.length));
            return;
        }
        setWaitingForServer(true);
        onSubmit({});
        // Recovery if the request fails silently or the round trip stalls — otherwise the
        // wheel would be stuck disabled forever with no way to retry.
        waitTimeoutRef.current = setTimeout(() => setWaitingForServer(false), 10000);
    }, [spinning, waitingForServer, isLocked, hasSubmitted, allowSpinAgain, isPreview, onSubmit, segments, animateTo]);

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content?.question || 'Spin the wheel!'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>
            {content?.settings?.description && content?.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}
            {content?.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
            )}
            {/* Spinner Wheel — responsive, readable labels, two-line wrap for long text */}
            <div className={styles.spinnerWheelOuter}>
                <div className={styles.spinnerWheelWrap}>
                    <div className={styles.spinnerWheelPointer} aria-hidden />

                    <svg
                        ref={wheelRef}
                        className={styles.spinnerWheelSvg}
                        viewBox="0 0 200 200"
                        style={{
                            transform: `rotate(${rotation}deg)`,
                            transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
                        }}
                        aria-hidden
                    >
                        {segments.map((seg, i) => {
                            const angle = 360 / segments.length;
                            const startAngle = i * angle - 90;
                            const endAngle = startAngle + angle;
                            const startRad = (startAngle * Math.PI) / 180;
                            const endRad = (endAngle * Math.PI) / 180;
                            const x1 = 100 + 92 * Math.cos(startRad);
                            const y1 = 100 + 92 * Math.sin(startRad);
                            const x2 = 100 + 92 * Math.cos(endRad);
                            const y2 = 100 + 92 * Math.sin(endRad);
                            const largeArc = angle > 180 ? 1 : 0;
                            const midAngleRad = ((startAngle + endAngle) / 2 * Math.PI) / 180;
                            const labelX = 100 + 58 * Math.cos(midAngleRad);
                            const labelY = 100 + 58 * Math.sin(midAngleRad);
                            const color = WHEEL_COLORS[i % WHEEL_COLORS.length];
                            const textRotation = (startAngle + endAngle) / 2;
                            const lines = splitLabel(seg, segments.length > 6 ? 10 : 14);
                            const twoLines = lines.length > 1;
                            const fontSize = segments.length > 6 ? (twoLines ? 6 : 7) : (twoLines ? 7 : 8);
                            const lineHeight = fontSize * 1.15;

                            return (
                                <g key={i}>
                                    <path
                                        d={`M100,100 L${x1},${y1} A92,92 0 ${largeArc},1 ${x2},${y2} Z`}
                                        fill={color}
                                        stroke="rgba(255,255,255,0.35)"
                                        strokeWidth="1.2"
                                    />
                                    <text
                                        x={labelX}
                                        y={labelY}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fill="#fff"
                                        fontSize={fontSize}
                                        fontWeight="600"
                                        transform={`rotate(${textRotation}, ${labelX}, ${labelY})`}
                                        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                                    >
                                        {twoLines ? (
                                            <>
                                                <tspan x={labelX} dy={-lineHeight / 2}>{lines[0]}</tspan>
                                                <tspan x={labelX} dy={lineHeight}>{lines[1]}</tspan>
                                            </>
                                        ) : (
                                            lines[0]
                                        )}
                                    </text>
                                </g>
                            );
                        })}
                        <circle cx="100" cy="100" r="18" fill="#fff" stroke="rgba(0,0,0,0.08)" strokeWidth="2" />
                        <circle cx="100" cy="100" r="6" fill="#6366f1" />
                    </svg>
                </div>

                {displayResult && (
                    <div className={styles.spinnerWheelResult}>
                        <span className={styles.spinnerWheelResultLabel}>Your result</span>
                        <PartyPopper size={16} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        {displayResult}
                    </div>
                )}
            </div>

            <button
                className={styles.submitBtn}
                onClick={spin}
                disabled={isPreview || spinDisabled}
            >
                {waitingForServer
                    ? 'Drawing...'
                    : spinning
                    ? 'Spinning...'
                    : isPreview
                        ? 'Preview only'
                        : isLocked
                            ? 'Voting locked'
                            : hasSubmitted && !allowSpinAgain
                                ? "You've already spun"
                                : hasSubmitted
                                    ? 'Spin again'
                                    : 'Spin!'}
            </button>
        </div>
    );
};

export default SpinnerWheelForm;
